import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Users, 
  Mail, 
  Download, 
  RefreshCw, 
  Search,
  Calendar,
  TrendingUp,
  ArrowLeft,
  Copy,
  Check,
  Trash2,
  Lock,
  Eye,
  EyeOff
} from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const LoginScreen = ({ onLogin }) => {
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      await axios.post(`${BACKEND_URL}/api/admin/login`, { password });
      localStorage.setItem("ghost_admin_password", password);
      onLogin(password);
    } catch (err) {
      setError("Invalid password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#05050A] flex items-center justify-center p-6">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="w-full max-w-md p-8 rounded-3xl glass"
      >
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#00D4FF] to-[#8B5CF6] flex items-center justify-center mx-auto mb-4">
            <Lock size={32} className="text-white" />
          </div>
          <h1 className="font-['Unbounded'] text-2xl font-bold text-white mb-2">
            Admin Access
          </h1>
          <p className="text-[#A1A1AA] text-sm">
            Enter password to access the dashboard
          </p>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="relative mb-6">
            <input
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter admin password"
              className="w-full px-4 py-4 pr-12 rounded-xl glass text-white placeholder-[#A1A1AA] focus:outline-none focus:ring-2 focus:ring-[#00D4FF]/50"
              data-testid="admin-password-input"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-[#A1A1AA] hover:text-white transition-colors"
            >
              {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
            </button>
          </div>

          {error && (
            <p className="text-[#EF4444] text-sm text-center mb-4">{error}</p>
          )}

          <button
            type="submit"
            disabled={loading || !password}
            className="w-full py-4 rounded-xl bg-gradient-to-r from-[#00D4FF] to-[#8B5CF6] text-white font-bold hover:shadow-[0_0_30px_rgba(0,212,255,0.4)] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="admin-login-button"
          >
            {loading ? (
              <RefreshCw size={20} className="animate-spin mx-auto" />
            ) : (
              "Access Dashboard"
            )}
          </button>
        </form>

        <p className="text-center text-xs text-[#A1A1AA]/50 mt-6">
          Default password: ghost2025
        </p>
      </motion.div>
    </div>
  );
};

const AdminDashboard = ({ onBack }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [adminPassword, setAdminPassword] = useState("");
  const [waitlist, setWaitlist] = useState([]);
  const [stats, setStats] = useState({ count: 0, remaining_spots: 500 });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [copiedId, setCopiedId] = useState(null);

  useEffect(() => {
    const savedPassword = localStorage.getItem("ghost_admin_password");
    if (savedPassword) {
      // Verify saved password is still valid
      axios.post(`${BACKEND_URL}/api/admin/login`, { password: savedPassword })
        .then(() => {
          setAdminPassword(savedPassword);
          setIsAuthenticated(true);
        })
        .catch(() => {
          localStorage.removeItem("ghost_admin_password");
        });
    }
  }, []);

  const fetchData = async () => {
    if (!adminPassword) return;
    
    setLoading(true);
    try {
      const [waitlistRes, statsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/admin/waitlist`, {
          headers: { "X-Admin-Password": adminPassword }
        }),
        axios.get(`${BACKEND_URL}/api/waitlist/count`)
      ]);
      setWaitlist(waitlistRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      if (error.response?.status === 401) {
        localStorage.removeItem("ghost_admin_password");
        setIsAuthenticated(false);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated && adminPassword) {
      fetchData();
    }
  }, [isAuthenticated, adminPassword]);

  const handleLogin = (password) => {
    setAdminPassword(password);
    setIsAuthenticated(true);
  };

  const handleLogout = () => {
    localStorage.removeItem("ghost_admin_password");
    setIsAuthenticated(false);
    setAdminPassword("");
    onBack();
  };

  const handleCopyEmail = (email, id) => {
    navigator.clipboard.writeText(email);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleExportCSV = () => {
    const headers = ["Email", "Signed Up Date", "ID"];
    const csvContent = [
      headers.join(","),
      ...waitlist.map(entry => [
        entry.email,
        new Date(entry.timestamp).toLocaleDateString(),
        entry.id
      ].join(","))
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `ghost-waitlist-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleDeleteEntry = async (id) => {
    if (!window.confirm("Are you sure you want to remove this email from the waitlist?")) return;
    
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/waitlist/${id}`, {
        headers: { "X-Admin-Password": adminPassword }
      });
      fetchData();
    } catch (error) {
      console.error("Error deleting entry:", error);
    }
  };

  if (!isAuthenticated) {
    return <LoginScreen onLogin={handleLogin} />;
  }

  const filteredWaitlist = waitlist.filter(entry =>
    entry.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const percentFilled = ((500 - stats.remaining_spots) / 500) * 100;

  return (
    <div className="min-h-screen bg-[#05050A] text-white p-6 md:p-12" data-testid="admin-dashboard">
      {/* Header */}
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <button
              onClick={onBack}
              className="p-2 rounded-lg glass ice-hover"
              data-testid="admin-back-button"
            >
              <ArrowLeft size={20} />
            </button>
            <div>
              <h1 className="font-['Unbounded'] text-2xl md:text-3xl font-bold">
                Admin Dashboard
              </h1>
              <p className="text-[#A1A1AA] text-sm">Manage your GHOST waitlist</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={fetchData}
              className="flex items-center gap-2 px-4 py-2 rounded-lg glass ice-hover"
              disabled={loading}
              data-testid="admin-refresh-button"
            >
              <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
              Refresh
            </button>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#EF4444]/20 text-[#EF4444] hover:bg-[#EF4444]/30 transition-colors"
            >
              <Lock size={16} />
              Logout
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid md:grid-cols-4 gap-6 mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="p-6 rounded-2xl glass"
          >
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-[#00D4FF]/20 flex items-center justify-center">
                <Users size={20} className="text-[#00D4FF]" />
              </div>
              <span className="text-[#A1A1AA] text-sm">Total Signups</span>
            </div>
            <div className="font-['Unbounded'] text-3xl font-bold gradient-text">
              {stats.count}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="p-6 rounded-2xl glass"
          >
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-[#8B5CF6]/20 flex items-center justify-center">
                <Mail size={20} className="text-[#8B5CF6]" />
              </div>
              <span className="text-[#A1A1AA] text-sm">Spots Remaining</span>
            </div>
            <div className="font-['Unbounded'] text-3xl font-bold text-white">
              {stats.remaining_spots}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="p-6 rounded-2xl glass"
          >
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-[#10B981]/20 flex items-center justify-center">
                <TrendingUp size={20} className="text-[#10B981]" />
              </div>
              <span className="text-[#A1A1AA] text-sm">Fill Rate</span>
            </div>
            <div className="font-['Unbounded'] text-3xl font-bold text-[#10B981]">
              {percentFilled.toFixed(1)}%
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="p-6 rounded-2xl glass"
          >
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-[#FF3366]/20 flex items-center justify-center">
                <Calendar size={20} className="text-[#FF3366]" />
              </div>
              <span className="text-[#A1A1AA] text-sm">Today's Signups</span>
            </div>
            <div className="font-['Unbounded'] text-3xl font-bold text-white">
              {waitlist.filter(e => {
                const today = new Date().toDateString();
                return new Date(e.timestamp).toDateString() === today;
              }).length}
            </div>
          </motion.div>
        </div>

        {/* Progress Bar */}
        <div className="p-6 rounded-2xl glass mb-8">
          <div className="flex justify-between items-center mb-3">
            <span className="text-sm text-[#A1A1AA]">Waitlist Progress</span>
            <span className="text-sm font-mono text-[#00D4FF]">{stats.count} / 500</span>
          </div>
          <div className="w-full h-3 bg-white/10 rounded-full overflow-hidden">
            <motion.div
              className="h-full bg-gradient-to-r from-[#00D4FF] to-[#8B5CF6] rounded-full"
              initial={{ width: 0 }}
              animate={{ width: `${percentFilled}%` }}
              transition={{ duration: 1 }}
            />
          </div>
        </div>

        {/* Actions Bar */}
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#A1A1AA]" />
            <input
              type="text"
              placeholder="Search emails..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-3 rounded-xl glass text-white placeholder-[#A1A1AA] focus:outline-none focus:ring-2 focus:ring-[#00D4FF]/50"
              data-testid="admin-search-input"
            />
          </div>
          <button
            onClick={handleExportCSV}
            className="flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-[#00D4FF] to-[#8B5CF6] text-white font-semibold hover:shadow-[0_0_30px_rgba(0,212,255,0.4)] transition-all"
            data-testid="admin-export-button"
          >
            <Download size={18} />
            Export CSV
          </button>
        </div>

        {/* Waitlist Table */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="rounded-2xl glass overflow-hidden"
        >
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="admin-waitlist-table">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left p-4 text-[#A1A1AA] text-sm font-medium">Email</th>
                  <th className="text-left p-4 text-[#A1A1AA] text-sm font-medium">Signed Up</th>
                  <th className="text-left p-4 text-[#A1A1AA] text-sm font-medium">ID</th>
                  <th className="text-right p-4 text-[#A1A1AA] text-sm font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={4} className="p-8 text-center text-[#A1A1AA]">
                      <RefreshCw size={24} className="animate-spin mx-auto mb-2" />
                      Loading...
                    </td>
                  </tr>
                ) : filteredWaitlist.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="p-8 text-center text-[#A1A1AA]">
                      {searchTerm ? "No matching emails found" : "No signups yet"}
                    </td>
                  </tr>
                ) : (
                  filteredWaitlist.map((entry, index) => (
                    <motion.tr
                      key={entry.id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.05 }}
                      className="border-b border-white/5 hover:bg-white/5 transition-colors"
                    >
                      <td className="p-4">
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-sm">{entry.email}</span>
                          <button
                            onClick={() => handleCopyEmail(entry.email, entry.id)}
                            className="p-1 rounded hover:bg-white/10 transition-colors"
                          >
                            {copiedId === entry.id ? (
                              <Check size={14} className="text-[#10B981]" />
                            ) : (
                              <Copy size={14} className="text-[#A1A1AA]" />
                            )}
                          </button>
                        </div>
                      </td>
                      <td className="p-4 text-[#A1A1AA] text-sm">
                        {new Date(entry.timestamp).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </td>
                      <td className="p-4">
                        <span className="font-mono text-xs text-[#A1A1AA]">
                          {entry.id.slice(0, 8)}...
                        </span>
                      </td>
                      <td className="p-4 text-right">
                        <button
                          onClick={() => handleDeleteEntry(entry.id)}
                          className="p-2 rounded-lg hover:bg-[#EF4444]/20 text-[#A1A1AA] hover:text-[#EF4444] transition-colors"
                          title="Remove from waitlist"
                        >
                          <Trash2 size={16} />
                        </button>
                      </td>
                    </motion.tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </motion.div>

        {/* Footer note */}
        <p className="text-center text-xs text-[#A1A1AA]/50 mt-8">
          Tip: Click the GHOST logo in the footer 3 times to access this dashboard
        </p>
      </div>
    </div>
  );
};

export default AdminDashboard;
